"use client";

import { useEffect, useRef, useState } from "react";
import { SignInButton, useUser, CLERK_ENABLED } from "@/lib/auth-shim/client";

interface ReferralData {
    code: string;
    inviteUrl: string;
    referredCount: number;
    referralsPerReward: number;
    progress: number;
    monthsEarned: number;
    bonusActiveUntil: string | null;
}

function GiftIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            width={18}
            height={18}
        >
            <rect x="3" y="8" width="18" height="4" rx="1" />
            <path d="M12 8v13" />
            <path d="M19 12v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7" />
            <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8" />
            <path d="M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
        </svg>
    );
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

export function InviteFriendsButton() {
    const { isSignedIn, isLoaded } = useUser();
    const [open, setOpen] = useState(false);

    // Until Clerk resolves, neither branch is safe: opening the modal would
    // fire calls that 401, and showing sign-in would prompt a user who is
    // already signed in. Render the button inert for that window instead.
    if (CLERK_ENABLED && !isLoaded) {
        return (
            <button
                type="button"
                className="app-sidenav__invite"
                data-nav-label="Invite Friends"
                disabled
            >
                <GiftIcon />
                <span>Invite Friends</span>
            </button>
        );
    }

    // Without Clerk configured the shim reports signed-out and SignInButton
    // collapses to a pass-through, so this also covers no-auth local dev —
    // matching how the rest of the side-nav degrades.
    if (!isSignedIn) {
        return (
            <SignInButton mode="modal" forceRedirectUrl="/">
                <button
                    type="button"
                    className="app-sidenav__invite"
                    data-nav-label="Invite Friends"
                >
                    <GiftIcon />
                    <span>Invite Friends</span>
                </button>
            </SignInButton>
        );
    }

    return (
        <>
            <button
                type="button"
                className="app-sidenav__invite"
                data-nav-label="Invite Friends"
                onClick={() => setOpen(true)}
            >
                <GiftIcon />
                <span>Invite Friends</span>
            </button>
            {open && <InviteFriendsModal onClose={() => setOpen(false)} />}
        </>
    );
}

function InviteFriendsModal({ onClose }: { onClose: () => void }) {
    const [data, setData] = useState<ReferralData | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [copied, setCopied] = useState(false);
    const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === "Escape") onClose();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(
        () => () => {
            if (copyTimer.current) clearTimeout(copyTimer.current);
        },
        []
    );

    useEffect(() => {
        let cancelled = false;
        // Ensure the users row exists before asking for a referral code — a
        // freshly signed-in user may open this before the side-nav's own
        // /api/user call has resolved.
        (async () => {
            await fetch("/api/user").catch(() => {});
            if (cancelled) return;
            const res = await fetch("/api/referrals");
            if (cancelled) return;
            if (!res.ok) throw new Error("Failed to load referral data");
            const json = await res.json();
            if (!cancelled) setData(json);
        })().catch(() => {
            if (!cancelled) setLoadError(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    async function copyLink() {
        if (!data) return;
        try {
            await navigator.clipboard.writeText(data.inviteUrl);
            setCopied(true);
            if (copyTimer.current) clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard permission denied or unavailable — the link is
            // still visible and selectable in the input above.
        }
    }

    async function shareLink() {
        if (!data) return;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Join me on Signpost",
                    text: "Learn ASL fingerspelling with me on Signpost. Sign up with my link!",
                    url: data.inviteUrl,
                });
            } catch {
                // User canceled the native share sheet — nothing to do.
            }
        } else {
            copyLink();
        }
    }

    const bonusActive =
        Boolean(data?.bonusActiveUntil) &&
        new Date(data!.bonusActiveUntil as string) > new Date();

    return (
        <div
            className="invite-modal-backdrop"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Invite Friends"
        >
            <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="invite-modal-close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ✕
                </button>

                <div className="invite-modal-icon" aria-hidden="true">
                    <GiftIcon />
                </div>

                <div className="invite-modal-eyebrow">Invite Friends</div>
                <h2 className="invite-modal-title">Give a month, get a month</h2>
                <p className="invite-modal-desc">
                    Invite 5 friends to Signpost. For every 5 who sign up with your
                    link, you earn a free month of Pro, automatically.
                </p>

                {loadError && (
                    <p className="invite-modal-error">
                        Couldn&apos;t load your invite link. Try again shortly.
                    </p>
                )}

                {!loadError && !data && (
                    <div className="invite-modal-skeleton" aria-hidden="true" />
                )}

                {data && (
                    <>
                        {bonusActive && (
                            <div className="invite-modal-bonus">
                                🎉 {data.monthsEarned} free month
                                {data.monthsEarned === 1 ? "" : "s"} of Pro earned,
                                active until {formatDate(data.bonusActiveUntil as string)}
                            </div>
                        )}

                        <div className="invite-modal-link-row">
                            <input
                                type="text"
                                readOnly
                                value={data.inviteUrl}
                                className="invite-modal-link-input"
                                onFocus={(e) => e.currentTarget.select()}
                            />
                            <button
                                type="button"
                                className="invite-modal-copy"
                                onClick={copyLink}
                            >
                                {copied ? "Copied!" : "Copy"}
                            </button>
                        </div>

                        <button
                            type="button"
                            className="invite-modal-share"
                            onClick={shareLink}
                        >
                            Share your invite link
                        </button>

                        <div className="invite-modal-progress">
                            <div className="invite-modal-progress-label">
                                <span>
                                    {data.progress} of {data.referralsPerReward} friends
                                    joined
                                </span>
                                <span>
                                    {data.referralsPerReward - data.progress} to go
                                </span>
                            </div>
                            <div className="invite-modal-progress-track">
                                <div
                                    className="invite-modal-progress-fill"
                                    style={{
                                        width: `${(data.progress / data.referralsPerReward) * 100}%`,
                                    }}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
